// Third Party
import React from 'react';
import { compact, map } from 'lodash';
import Dotdotdot from 'react-dotdotdot';

// Components
import { Tooltip } from '@/views/components/tooltips';
import ComparisonCardHoldingIcon from '../../../../../../ComparisonTable/ComparisonCardHoldingIcon';
import ComparisonRiskProfileLabel from '../../../../../../misc/ComparisonRiskProfileLabel';
import { CurrencyLabel } from '@/views/components/misc';
import { ComparisonDisclaimer } from './components/ComparisonDisclaimer';
import BenchmarkCollapsible from './components/BenchmarkCollapsible';

// Utils
import { formatLocalizedString } from '@/utils/i18n';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { getShortHistoryDatesForItem } from '@/utils/disclaimers/comparison';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';

type Props = {
  comparison: Comparison;
  item: ComparisonItem;
  user?: Nullable<User>;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
};

const NAV_FILTER = 'nav';

const HistoricalScenariosComparisonHeader: React.FC<Props> = ({
  comparison,
  item,
  user,
  updateComparisonItemColor,
  popoverContainerRef,
  scrollbarContainerRef,
}) => {
  const itemName = formatLocalizedString(item.name);
  const shortHistoryDates = getShortHistoryDatesForItem(item, undefined, NAV_FILTER);
  const showLongerHistory = Boolean(comparison.shortHistory && shortHistoryDates.length > 0);
  const showCurrency = comparison.showBaseCurrencies && userHasPermissionEnabled(user, PERMISSIONS.CURRENCY_CONVERSIONS);
  const profileToShow = comparison.riskProfile || item.riskProfile;
  const itemSymbols = compact(map(item.holdings, 'symbol')).join(', ');

  return (
    <>
      <div className="top-row">
        <div>
          {item.holdings.length > 0 && (
            <div className="historical-scenario-header-title">
              <ComparisonCardHoldingIcon
                item={item}
                updateComparisonItemColor={updateComparisonItemColor}
                popoverContainerRef={popoverContainerRef}
                scrollbarContainerRef={scrollbarContainerRef}
              />
              <span className="item-symbols">
                <Dotdotdot useNativeClamp clamp={1}>
                  {itemSymbols}
                </Dotdotdot>
              </span>
              {profileToShow ? <ComparisonRiskProfileLabel profile={profileToShow} isComparisonRiskProfile={comparison.riskProfile !== undefined} /> : null}
            </div>
          )}
          <div className="historical-scenario-item-name">
            <Tooltip message={itemName} delayShow={300} data-private data-dd-privacy="mask">
              <span id={`row-${item._id}`}>
                <Dotdotdot useNativeClamp clamp={2}>
                  {itemName}
                </Dotdotdot>
              </span>
            </Tooltip>
            <div className="historical-scenario-item-labels">{showCurrency && item.currency && <CurrencyLabel currency={item.currency} />}</div>
          </div>
        </div>
      </div>
      <ComparisonDisclaimer item={item} showLongerHistory={showLongerHistory} shortHistoryDates={shortHistoryDates} />
      {item.type === COMPARISON_ITEM_TYPES.BENCHMARK && <BenchmarkCollapsible user={user} comparison={comparison} item={item} />}
    </>
  );
};

export default HistoricalScenariosComparisonHeader;
